import { forwardRef, useEffect, useImperativeHandle } from "react";
import { DataTable } from "primereact/datatable";
import { useAPIGetPackageRequest } from "../api/api-get-request-expired-date";
import { Column } from "primereact/column";
import PackageRequestFilter from "./request-expired-date-filter";
import { Paginator } from "primereact/paginator";
import { Button } from "primereact/button";
import { format } from "date-fns";
import useIsMobile from "../../../../hooks/useIsMobile";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

const PackageRequestList = forwardRef((props, ref) => {
  const isMobile = useIsMobile();

  const {
    loading,
    loadingMain,
    listData,
    fetchListData,
    filterValue,
    setFilterValue,
    handleOnClickRefresh,
    handleOnPage,
    paging,
    handleOnUpdateViewRow,
    handleOnDeleteViewRow,
    handleApprove,
    handleReject,
  } = useAPIGetPackageRequest();

  useImperativeHandle(ref, () => ({
    handleOnUpdateViewRow: (row) => {
      handleOnUpdateViewRow(row);
    },
    handleOnDeleteViewRow: (row) => {
      handleOnDeleteViewRow(row);
    },
  }));

  // use effect section

  useEffect(() => {
    fetchListData({ page: paging.page, take: paging.take });
  }, []);

  const confirmApprove = (row) => {
    confirmDialog({
      message: `Are you sure you want to approve this request?`,
      header: 'Approve Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-success',
      accept: () => {
        handleApprove(row);
      },
    });
  };

  const confirmReject = (row) => {
    confirmDialog({
      message: `Are you sure you want to reject this request?`,
      header: 'Reject Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => {
        handleReject(row);
      },
    });
  };

  //  custom ui section

  const CustomerBodyTemplate = (row) => {
    const { data_customer } = row;

    return <div className="col-start-start gap-1">
      <span>{data_customer?.name} ({data_customer?.code})</span>
      <span className="text-sm font-semibold">BC: {data_customer?.data_bc?.name}</span>
    </div>
  };

  const ProductBodyTemplate = (row) => {
    const { expired_date, request_expired_date, data_product, description } = row;

    return <div className="col-start-start gap-1">
      <span className="font-bold text-indigo-700">{data_product?.name}</span>
      <span className="text-sm">Current Expired: <span className="font-semibold text-red-600">{expired_date ? format(new Date(expired_date), "dd MMMM yyyy") : '-'}</span></span>
      <span className="text-sm">Req. Expired: <span className="font-semibold text-green-600">{request_expired_date ? format(new Date(request_expired_date), "dd MMMM yyyy") : '-'}</span></span>
      {description && <span className="text-sm text-gray-600 mt-1 italic">Note: {description}</span>}
    </div>
  };

  const actionBodyTemplate = (row) => {
    return (
      <div className="row-center-center gap-2">
        <Button
          icon="pi pi-check"
          rounded
          text
          severity="success"
          onClick={() => confirmApprove(row)}
          tooltip="Approve"
          tooltipOptions={{ position: 'top' }}
          className="!w-8 !h-8 bg-green-50 hover:bg-green-100"
        />
        <Button
          icon="pi pi-times"
          rounded
          text
          severity="danger"
          onClick={() => confirmReject(row)}
          tooltip="Reject"
          tooltipOptions={{ position: 'top' }}
          className="!w-8 !h-8 bg-red-50 hover:bg-red-100"
        />
      </div>
    );
  };

  const MobileCardItem = ({ row }) => {
    const { data_customer, data_product, expired_date, request_expired_date, description } = row;

    return (
      <div className="elevated-card p-4 col-start-start gap-3 w-full border-l-4 border-indigo-500">
        {/* Customer Info */}
        <div className="col-start-start gap-1 w-full">
          <div className="row-start-center gap-2">
            <i className="pi pi-user text-indigo-600"></i>
            <span className="font-semibold text-sm text-gray-500">Customer</span>
          </div>
          <span className="font-bold">{data_customer?.name} ({data_customer?.code})</span>
          <span className="text-sm text-gray-600">BC: {data_customer?.data_bc?.name}</span>
        </div>

        {/* Product Info */}
        <div className="col-start-start gap-1 w-full bg-slate-50 p-3 rounded-lg border border-slate-100">
          <div className="row-start-center gap-2 mb-1">
            <i className="pi pi-shopping-bag text-purple-600"></i>
            <span className="font-semibold text-sm text-gray-500">Request Details</span>
          </div>
          <span className="font-bold text-indigo-700">{data_product?.name}</span>
          <div className="row-between-center w-full mt-2">
            <div className="col-start-start gap-0">
              <span className="text-[10px] text-gray-500 uppercase font-bold">Current</span>
              <span className="text-sm font-semibold text-red-600 line-through decoration-red-300">{expired_date ? format(new Date(expired_date), "dd MMM yyyy") : '-'}</span>
            </div>
            <i className="pi pi-arrow-right text-gray-400"></i>
            <div className="col-end-end gap-0">
              <span className="text-[10px] text-gray-500 uppercase font-bold">Requested</span>
              <span className="text-sm font-semibold text-green-600">{request_expired_date ? format(new Date(request_expired_date), "dd MMM yyyy") : '-'}</span>
            </div>
          </div>
          {description && <span className="text-sm text-gray-700 mt-2 bg-yellow-50 p-2 rounded w-full border border-yellow-100">Note: {description}</span>}
        </div>

        {/* Actions */}
        <div className="row-center-center gap-2 w-full pt-2">
          <Button
            icon="pi pi-check"
            label="Approve"
            severity="success"
            className="flex-1 shadow-sm"
            onClick={() => confirmApprove(row)}
          />
          <Button
            icon="pi pi-times"
            label="Reject"
            severity="danger"
            className="flex-1 shadow-sm"
            onClick={() => confirmReject(row)}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="col-start-start w-full gap-4">
      {/* filter section */}
      <div className="elevated-card-tight-vertical row-start-start w-full gap-4">
        <PackageRequestFilter
          filterValue={filterValue}
          setFilterValue={setFilterValue}
          onClickRefresh={handleOnClickRefresh}
        />
      </div>

      {/* Main Requests Section */}
      <div className="elevated-card-tight-vertical col-start-center w-full gap-2">
        <div className="row-between-center w-full pb-2 border-b border-gray-200">
          <div className="row-start-center gap-2">
            <i className="pi pi-calendar-clock text-indigo-600 text-xl"></i>
            <h3 className="text-lg font-bold text-indigo-700">Expire Date Change Requests</h3>
            {listData && listData.length > 0 && (
              <span className="bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full text-sm font-semibold">
                {listData.length}
              </span>
            )}
          </div>
        </div>

        {isMobile ? (
          // Mobile Card View
          <div className="col-start-start w-full gap-3 pt-2">
            {loadingMain ? (
              <div className="col-center-center w-full py-8">
                <i className="pi pi-spin pi-spinner text-3xl text-indigo-500"></i>
              </div>
            ) : listData && listData.length > 0 ? (
              listData.map((row) => <MobileCardItem key={row.id} row={row} />)
            ) : (
              <div className="col-center-center w-full py-8 text-gray-500">
                <i className="pi pi-inbox text-4xl mb-2"></i>
                <span>No requests found</span>
              </div>
            )}
          </div>
        ) : (
          // Desktop Table View
          <DataTable
            dataKey="id"
            className="w-100"
            loading={loadingMain}
            showGridlines
            stripedRows
            value={listData}
            emptyMessage="No requests found"
          >
            <Column
              field="no"
              header="#"
              className="text-center"
              headerClassName="w-[4rem]"
              alignHeader={"center"}
            />
            <Column
              field=""
              header="Action"
              alignHeader={"center"}
              headerClassName="w-[10rem]"
              body={actionBodyTemplate}
            />
            <Column header='Customer' field='data_customer.name' body={CustomerBodyTemplate} headerClassName="w-[16rem]" />
            <Column header='Request Details' field='data_product.name' body={ProductBodyTemplate} />
          </DataTable>
        )}

        {isMobile ? (
          <Paginator
            first={parseInt(paging.take) * (paging.page - 1)}
            rows={parseInt(paging.take)}
            totalRecords={parseInt(paging.total)}
            template={{ layout: "PrevPageLink CurrentPageReport NextPageLink" }}
            onPageChange={handleOnPage}
            pt={{
              prevPageButton: {
                className:
                  "bg-indigo-50 !m-0 !max-w-[22px] !w-[22px] !min-w-[22px] max-h-[22px]",
              },
              nextPageButton: {
                className:
                  "bg-indigo-50 !m-0 !max-w-[22px] !w-[22px] !min-w-[22px] max-h-[22px]",
              },
              current: {
                className: "bg-indigo-50 !m-0 max-h-[22px] text-sm",
              },
            }}
          />
        ) : (
          <Paginator
            first={parseInt(paging.take) * (paging.page - 1)}
            rows={parseInt(paging.take)}
            totalRecords={parseInt(paging.total)}
            rowsPerPageOptions={[5, 10, 20, 50]}
            onPageChange={handleOnPage}
          />
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog />
    </div>
  );
});

export default PackageRequestList;
